import {
  FlexBox,
  HeartIcon,
  JoonColorExpanded,
  JoonUIColor,
  SPACING,
  TextButton,
  Typography,
} from "@joonapp/web-shared"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import Xarrow, { Xwrapper } from "react-xarrows"

import ListCard from "../../../components/coaching/ListCard"
import TextArrowButton from "../../../components/coaching/TextArrowButton"
import PageLoader from "../../../components/loading/PageLoader"
import { useCoachIntakeFormQuestionnaireQuery } from "../../../networking/queries"
import { CoachingStep } from "../../../types/coachingGroups"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../../util/analytics"
import { localStorageItems } from "../../../util/storage"
import { getTopValueProps } from "../getTopValueProps"
import { useCoachingStore } from "../useCoachingStore"
import useIsFromMOF from "../useIsFromMOF"
import useSkipCoachingStep from "../useSkipCoachingStep"

const CoachLandingPage = () => {
  const { setStep, setQuestionnaireStep } = useCoachingStore()
  const { data: coachQuestionnaire, isLoading } =
    useCoachIntakeFormQuestionnaireQuery()
  const isFromMOF = useIsFromMOF()
  const skipCoachingStep = useSkipCoachingStep()
  const { t } = useTranslation()

  const parentQuestionnaireAnswers = JSON.parse(
    localStorage.getItem(localStorageItems.parentQuestionnaireAnswers) || "{}"
  )

  const valueProps = getTopValueProps(parentQuestionnaireAnswers, t)

  const onContinueClick = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.MARKETING_CONTINUE_CLICK, {
      step: CoachingStep.LANDING,
      is_tof: !isFromMOF,
    })
    if (coachQuestionnaire?.questions?.length) setStep(CoachingStep.INTAKE_FORM)
    else setStep(CoachingStep.MATCHING)
  }

  const onSkipClick = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.MARKETING_SKIP_CLICK, {
      step: CoachingStep.LANDING,
      is_tof: !isFromMOF,
    })
    skipCoachingStep()
  }

  useEffect(() => {
    setQuestionnaireStep(0)
  }, [setQuestionnaireStep])

  if (isLoading) return <PageLoader />

  return (
    <FlexBox
      direction="column"
      align="center"
      justify="center"
      fullWidth
      gap={SPACING.space6}
    >
      <FlexBox
        direction="column"
        align="center"
        justify="center"
        gap={SPACING.space2}
        fullWidth
      >
        <Typography variant="h2" color={JoonUIColor.text.primary}>
          {t("COACHING_LANDING_TITLE")}
        </Typography>
        <img
          src="/images/guidePhotos/coach_amy_help.svg"
          alt="Helpful message from Coach Amy"
          style={{ width: "min(80vw, 300px)", minHeight: "100px" }}
        />
      </FlexBox>
      <Typography variant="bodySmallBold" color={JoonUIColor.text.primary}>
        {t("COACHING_LANDING_SUBTITLE")}
      </Typography>

      <FlexBox
        direction="column"
        align="center"
        justify="center"
        fullWidth
        gap={SPACING.space2}
      >
        <FlexBox direction="column" style={{ maxWidth: "300px" }}>
          <Xwrapper>
            <FlexBox direction="row" justify="flex-start">
              <ListCard
                id={"elem1"}
                text={`😢 ${valueProps[0].problem}`}
                backgroundColor={JoonColorExpanded.red100}
                style={{
                  borderRadius: "100px",
                  width: "fit-content",
                  padding: SPACING.space2,
                  marginLeft: valueProps[0].problem.length > 30 ? "1%" : "5%",
                }}
              />
            </FlexBox>
            <FlexBox direction="row" justify="flex-end" wrap={false}>
              <ListCard
                id={"elem2"}
                icon={HeartIcon}
                iconColor={JoonColorExpanded.yellow400}
                text={valueProps[0].valueProp}
                backgroundColor={JoonColorExpanded.yellow100}
                gap={SPACING.space2}
                style={{
                  borderRadius: "100px",
                  width: "fit-content",
                  padding: SPACING.space2,
                  marginRight:
                    valueProps[0].valueProp.length > 30 ? "1%" : "5%",
                }}
              />
            </FlexBox>
            <Xarrow
              start="elem1"
              end="elem2"
              startAnchor="right"
              endAnchor="right"
              color={JoonColorExpanded.purple600}
              strokeWidth={2}
              headSize={-4}
              curveness={-0.5}
              _cpx1Offset={
                valueProps[0].valueProp.length - valueProps[0].problem.length >
                10
                  ? 50
                  : 100
              }
              _cpx2Offset={16}
              _cpy1Offset={-3}
              _cpy2Offset={-20}
            />
          </Xwrapper>
          <Xwrapper>
            <FlexBox direction="row" justify="flex-start">
              <ListCard
                id={"elem3"}
                text={`😢 ${valueProps[1].problem}`}
                backgroundColor={JoonColorExpanded.red100}
                style={{
                  borderRadius: "100px",
                  width: "fit-content",
                  padding: SPACING.space2,
                }}
              />
            </FlexBox>
            <FlexBox direction="row" justify="flex-end">
              <ListCard
                id={"elem4"}
                icon={HeartIcon}
                iconColor={JoonColorExpanded.yellow400}
                text={valueProps[1].valueProp}
                backgroundColor={JoonColorExpanded.yellow100}
                gap={SPACING.space2}
                style={{
                  borderRadius: "100px",
                  width: "fit-content",
                  padding: SPACING.space2,
                }}
              />
            </FlexBox>
            <Xarrow
              start="elem3"
              end="elem4"
              startAnchor="left"
              endAnchor="left"
              color={JoonColorExpanded.purple600}
              strokeWidth={2}
              headSize={4}
              curveness={0.8}
              _cpx1Offset={
                valueProps[1].valueProp.length - valueProps[1].problem.length >
                10
                  ? -50
                  : -100
              }
              _cpx2Offset={-20}
              _cpy1Offset={10}
              _cpy2Offset={0}
            />
          </Xwrapper>
          <FlexBox direction="row" justify="flex-start">
            <ListCard
              id={"elem5"}
              text={`😢 ${valueProps[2].problem}`}
              backgroundColor={JoonColorExpanded.red100}
              style={{
                borderRadius: "100px",
                width: "fit-content",
                padding: SPACING.space2,
                marginLeft: valueProps[2].problem.length > 30 ? "1%" : "5%",
              }}
            />
          </FlexBox>
          <Xwrapper>
            <FlexBox direction="row" justify="flex-end">
              <ListCard
                id={"elem6"}
                icon={HeartIcon}
                iconColor={JoonColorExpanded.yellow400}
                text={valueProps[2].valueProp}
                backgroundColor={JoonColorExpanded.yellow100}
                gap={SPACING.space2}
                style={{
                  borderRadius: "100px",
                  width: "fit-content",
                  padding: SPACING.space2,
                  marginRight:
                    valueProps[2].valueProp.length > 30 ? "1%" : "5%",
                }}
              />
            </FlexBox>
            <Xarrow
              start="elem5"
              end="elem6"
              startAnchor="right"
              endAnchor="right"
              color={JoonColorExpanded.purple600}
              strokeWidth={2}
              headSize={-4}
              curveness={-0.5}
              _cpx1Offset={
                valueProps[2].valueProp.length - valueProps[2].problem.length >
                10
                  ? 50
                  : 100
              }
              _cpx2Offset={20}
              _cpy1Offset={-3}
              _cpy2Offset={-20}
            />
          </Xwrapper>
        </FlexBox>

        <Typography
          variant="bodySmall"
          color={JoonUIColor.text.primary}
          style={{ marginTop: SPACING.space2, marginBottom: SPACING.space2 }}
        >
          {t("COACHING_LANDING_DESC_1")} <br />
          {t("COACHING_LANDING_DESC_2")}
        </Typography>
        <div
          style={{ position: "sticky", width: "100%", bottom: SPACING.space4 }}
        >
          <TextArrowButton
            text={t("COACHING_LANDING_CTA")}
            onClick={onContinueClick}
            style={{
              position: "relative",
              width: "100%",
            }}
          />
        </div>
        <TextButton onClick={onSkipClick}>
          <Typography variant="bodyBold" color={JoonUIColor.text.primaryAccent}>
            {t("COACHING_LANDING_SKIP")}
          </Typography>
        </TextButton>
      </FlexBox>
    </FlexBox>
  )
}

export default CoachLandingPage
